// src/pages/CompaniesPage.js
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import useAuth from '../hooks/useAuth';
import {
    Box, Button, Container, Typography, Stack, Paper, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, IconButton, Tooltip,
    alpha, CircularProgress, Chip, TextField,
    Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
    Snackbar, Alert,
    Accordion, AccordionSummary, AccordionDetails, List, ListItem, ListItemText, ListItemIcon,
    Select, MenuItem, FormControl, InputLabel,
    useMediaQuery, useTheme, Divider, Checkbox, FormGroup, FormControlLabel,
    CssBaseline, styled
} from '@mui/material';
import { Business, Delete, ExpandMore, Storefront, Edit, Group, Apps, Save, UploadFile, Download } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { ThemeProvider, createTheme } from '@mui/material/styles';

// --- TEMA IGUAL AO DA DASHBOARD ---
const customTheme = createTheme({
    palette: {
        mode: 'dark',
        primary: { main: '#315e9a', contrastText: '#ffffff' },
        secondary: { main: '#90caf9' },
        background: { default: '#0f1218', paper: '#1a1d24' },
        divider: 'rgba(0,0,0,0)', // evita erro do alpha('transparent')
    },
    typography: { button: { textTransform: 'none', fontWeight: 600 } },
    shape: { borderRadius: 12 },
});

// --- PAPER ESCURO PADRONIZADO ---
const DarkPaper = styled(Paper)(({ theme, selected }) => ({
    backgroundColor: selected ? alpha(theme.palette.primary.main, 0.15) : theme.palette.background.paper,
    backgroundImage: 'none',
    border: 'none',
    boxShadow: selected
        ? `0 0 0 1px ${alpha(theme.palette.primary.main, 0.5)}`
        : '0 8px 24px rgba(0,0,0,0.35)',
    transition: 'all 0.2s ease-in-out',
    borderRadius: 16,
}));

// --- ACCORDION ESCURO ---
const DarkAccordion = styled(Accordion)(({ theme }) => ({
    backgroundColor: 'transparent',
    backgroundImage: 'none',
    border: 'none',
    boxShadow: 'none',
    '&:before': { display: 'none' },
    '&.Mui-expanded': { margin: 0 },
}));

const CompaniesPage = () => {
    const { user } = useAuth();
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Estados para Exclusão
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [companyToDeleteId, setCompanyToDeleteId] = useState(null);
    const [companyToDeleteName, setCompanyToDeleteName] = useState('');

    // Estados para Edição
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [companyToEdit, setCompanyToEdit] = useState(null);
    const [editFormData, setEditFormData] = useState({ name: '', licenseType: '' });
    const [savingEdit, setSavingEdit] = useState(false);

    // Softwares
    const [openSoftwareDialog, setOpenSoftwareDialog] = useState(false);
    const [companyForSoftware, setCompanyForSoftware] = useState(null);
    const [allSoftwares, setAllSoftwares] = useState([]);
    const [selectedSoftwareIds, setSelectedSoftwareIds] = useState([]);
    const [savingSoftwares, setSavingSoftwares] = useState(false);

    // Bulk CSV
    const [openBulkSoftwareDialog, setOpenBulkSoftwareDialog] = useState(false);
    const [bulkSelectedSoftwareIds, setBulkSelectedSoftwareIds] = useState([]);
    const [bulkCompanyIds, setBulkCompanyIds] = useState([]);
    const [bulkCsvFileName, setBulkCsvFileName] = useState('');
    const [bulkInvalidRows, setBulkInvalidRows] = useState([]);
    const [bulkCsvError, setBulkCsvError] = useState('');
    const [bulkProcessing, setBulkProcessing] = useState(false);
    const [bulkProgress, setBulkProgress] = useState({ processed: 0, total: 0 });

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const fetchCompanies = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const response = await api.get('/companies');
            setCompanies(response.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Não foi possível carregar as empresas.');
        } finally { setLoading(false); }
    }, []);

    useEffect(() => {
        if (user && user.role === 'superadmin') {
            fetchCompanies();
            api.get('/softwares')
                .then(res => setAllSoftwares(res.data))
                .catch(err => console.error("Erro ao buscar catálogo de softwares:", err));
        } else if (user) {
            setError('Você não tem permissão para acessar esta página.');
            setLoading(false);
        } else {
            setLoading(false);
        }
    }, [user, fetchCompanies]);

    // --- SOFTWARES POR EMPRESA ---
    const handleOpenSoftwareDialog = (company) => {
        setCompanyForSoftware(company);
        const currentIds = company.softwares ? company.softwares.map(s => s.id) : [];
        setSelectedSoftwareIds(currentIds);
        setOpenSoftwareDialog(true);
    };
    const handleCloseSoftwareDialog = () => {
        setOpenSoftwareDialog(false);
        setCompanyForSoftware(null);
        setSelectedSoftwareIds([]);
    };
    const handleToggleSoftware = (softwareId) => {
        setSelectedSoftwareIds(prev =>
            prev.includes(softwareId) ? prev.filter(id => id !== softwareId) : [...prev, softwareId]
        );
    };
    const handleSaveSoftwares = async () => {
        if (!companyForSoftware) return;
        setSavingSoftwares(true);
        try {
            await api.post(`/softwares/company/${companyForSoftware.id}/assign`, { softwareIds: selectedSoftwareIds });
            setSnackbar({ open: true, message: 'Softwares atualizados com sucesso!', severity: 'success' });
            setCompanies(prev => prev.map(c => {
                if (c.id === companyForSoftware.id) {
                    const updatedSoftwares = allSoftwares.filter(s => selectedSoftwareIds.includes(s.id));
                    return { ...c, softwares: updatedSoftwares };
                }
                return c;
            }));
            handleCloseSoftwareDialog();
        } catch (err) {
            setSnackbar({ open: true, message: 'Erro ao salvar softwares.', severity: 'error' });
        } finally { setSavingSoftwares(false); }
    };

    // --- CSV EM MASSA ---
    const handleOpenBulkSoftwareDialog = () => {
        setBulkSelectedSoftwareIds([]); setBulkCompanyIds([]);
        setBulkCsvFileName(''); setBulkInvalidRows([]);
        setBulkCsvError(''); setBulkProgress({ processed: 0, total: 0 });
        setOpenBulkSoftwareDialog(true);
    };
    const handleCloseBulkSoftwareDialog = () => { if (bulkProcessing) return; setOpenBulkSoftwareDialog(false); };
    const handleToggleBulkSoftware = (softwareId) => {
        setBulkSelectedSoftwareIds(prev =>
            prev.includes(softwareId) ? prev.filter(id => id !== softwareId) : [...prev, softwareId]
        );
    };
    const normalizeCsvHeader = (value = '') =>
        String(value).trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[\s-]+/g, '_');

    const parseCsvLine = (line, delimiter) => {
        const result = []; let current = ''; let inQuotes = false;
        for (let i = 0; i < line.length; i += 1) {
            const char = line[i]; const next = line[i + 1];
            if (char === '"' && inQuotes && next === '"') { current += '"'; i += 1; }
            else if (char === '"') { inQuotes = !inQuotes; }
            else if (char === delimiter && !inQuotes) { result.push(current.trim()); current = ''; }
            else { current += char; }
        }
        result.push(current.trim());
        return result;
    };
    const normalizeCnpj = (value = '') => String(value).replace(/\D/g, '');

    const handleDownloadBulkTemplate = () => {
        const escapeCsv = (value) => { const str = String(value ?? ''); return `"${str.replace(/"/g, '""')}"`; };
        const rows = companies.map(company => {
            const matriz = company.units?.find(unit => unit.isMatriz) || company.units?.[0];
            return [company.id, company.name, matriz?.cnpj || ''].map(escapeCsv).join(';');
        });
        const content = ['empresa_id;empresa;cnpj_matriz', ...rows].join('\n');
        const blob = new Blob([`\uFEFF${content}`], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url; link.setAttribute('download', 'modelo_liberacao_softwares_empresas.csv');
        document.body.appendChild(link); link.click(); document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleBulkCsvFile = async (event) => {
        const file = event.target.files?.[0]; event.target.value = '';
        if (!file) return;
        setBulkCsvFileName(file.name); setBulkCompanyIds([]); setBulkInvalidRows([]); setBulkCsvError('');
        try {
            const rawText = await file.text();
            const cleanText = rawText.replace(/^\uFEFF/, '').replace(/\r/g, '');
            const lines = cleanText.split('\n').map(l => l.trim()).filter(Boolean);
            if (lines.length < 2) { setBulkCsvError('O CSV precisa ter o cabeçalho e pelo menos uma empresa.'); return; }
            const delimiter = (lines[0].match(/;/g) || []).length >= (lines[0].match(/,/g) || []).length ? ';' : ',';
            const headers = parseCsvLine(lines[0], delimiter).map(normalizeCsvHeader);
            const idColumnIndex = headers.findIndex(h => ['empresa_id', 'company_id', 'id_empresa', 'id'].includes(h));
            const cnpjColumnIndex = headers.findIndex(h => ['cnpj', 'cnpj_matriz', 'empresa_cnpj'].includes(h));
            if (idColumnIndex === -1 && cnpjColumnIndex === -1) {
                setBulkCsvError('Cabeçalho inválido. Use a coluna empresa_id ou cnpj_matriz.'); return;
            }
            const foundCompanies = new Map(); const invalidRows = [];
            lines.slice(1).forEach((line, index) => {
                const cells = parseCsvLine(line, delimiter);
                const informedId = idColumnIndex >= 0 ? String(cells[idColumnIndex] ?? '').trim() : '';
                const informedCnpj = cnpjColumnIndex >= 0 ? normalizeCnpj(cells[cnpjColumnIndex]) : '';
                let company = null;
                if (informedId) company = companies.find(item => String(item.id) === informedId) || null;
                if (!company && informedCnpj) company = companies.find(item => item.units?.some(unit => normalizeCnpj(unit.cnpj) === informedCnpj)) || null;
                if (company) foundCompanies.set(String(company.id), company.id);
                else invalidRows.push({ line: index + 2, value: informedId || informedCnpj || 'vazio' });
            });
            const validIds = Array.from(foundCompanies.values());
            setBulkCompanyIds(validIds); setBulkInvalidRows(invalidRows);
            if (validIds.length === 0) setBulkCsvError('Nenhuma empresa válida do CSV foi encontrada na lista atual.');
        } catch (err) {
            console.error('Erro ao ler CSV:', err);
            setBulkCsvError('Não foi possível ler o arquivo CSV.');
        }
    };

    const handleBulkAssignSoftwares = async () => {
        if (bulkSelectedSoftwareIds.length === 0) {
            setSnackbar({ open: true, message: 'Selecione pelo menos um software.', severity: 'warning' }); return;
        }
        if (bulkCompanyIds.length === 0) {
            setSnackbar({ open: true, message: 'Importe um CSV com pelo menos uma empresa válida.', severity: 'warning' }); return;
        }
        setBulkProcessing(true);
        setBulkProgress({ processed: 0, total: bulkCompanyIds.length });
        const targets = bulkCompanyIds.map(id => companies.find(c => String(c.id) === String(id))).filter(Boolean);
        const successfulUpdates = new Map();
        let successCount = 0; let failureCount = 0;
        const batchSize = 5;
        try {
            for (let start = 0; start < targets.length; start += batchSize) {
                const batch = targets.slice(start, start + batchSize);
                const results = await Promise.allSettled(batch.map(async company => {
                    const existingIds = company.softwares?.map(s => s.id) || [];
                    const mergedIds = [...existingIds];
                    bulkSelectedSoftwareIds.forEach(sid => {
                        if (!mergedIds.some(id => String(id) === String(sid))) mergedIds.push(sid);
                    });
                    await api.post(`/softwares/company/${company.id}/assign`, { softwareIds: mergedIds });
                    return { companyId: company.id, mergedIds };
                }));
                results.forEach(result => {
                    if (result.status === 'fulfilled') { successCount += 1; successfulUpdates.set(String(result.value.companyId), result.value.mergedIds); }
                    else { failureCount += 1; console.error('Erro em uma empresa da liberação em massa:', result.reason); }
                });
                setBulkProgress(prev => ({ ...prev, processed: Math.min(start + batch.length, targets.length) }));
            }
            if (successfulUpdates.size > 0) {
                setCompanies(prev => prev.map(company => {
                    const mergedIds = successfulUpdates.get(String(company.id));
                    if (!mergedIds) return company;
                    const updatedSoftwares = allSoftwares.filter(s => mergedIds.some(id => String(id) === String(s.id)));
                    return { ...company, softwares: updatedSoftwares };
                }));
            }
            if (failureCount === 0) {
                setSnackbar({
                    open: true,
                    message: `${successCount} empresa${successCount === 1 ? '' : 's'} atualizada${successCount === 1 ? '' : 's'} com sucesso!`,
                    severity: 'success'
                });
                setOpenBulkSoftwareDialog(false);
            } else {
                setSnackbar({
                    open: true,
                    message: `${successCount} empresa(s) atualizada(s) e ${failureCount} falharam. Tente novamente para as que falharam.`,
                    severity: 'warning'
                });
            }
        } finally { setBulkProcessing(false); }
    };

    // --- DELETE / EDIT ---
    const handleDeleteClick = (companyId, companyName) => {
        setCompanyToDeleteId(companyId); setCompanyToDeleteName(companyName); setOpenDeleteDialog(true);
    };
    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false); setCompanyToDeleteId(null); setCompanyToDeleteName('');
    };
    const handleConfirmDelete = async () => {
        if (!companyToDeleteId) return;
        try {
            const response = await api.delete(`/companies/${companyToDeleteId}`);
            setCompanies(prev => prev.filter(c => c.id !== companyToDeleteId));
            setSnackbar({ open: true, message: response.data.message || 'Empresa removida!', severity: 'warning' });
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.message || 'Não foi possível remover a empresa.', severity: 'error' });
        } finally { handleCloseDeleteDialog(); }
    };
    const handleEditClick = (company) => {
        setCompanyToEdit(company);
        setEditFormData({ name: company.name || '', licenseType: company.licenseType || '' });
        setOpenEditDialog(true);
    };
    const handleEditSubmit = async (e) => {
        e.preventDefault(); if (!companyToEdit) return;
        setSavingEdit(true);
        try {
            const response = await api.put(`/companies/${companyToEdit.id}`, editFormData);
            setCompanies(prev => prev.map(c => c.id === companyToEdit.id ? { ...c, ...response.data.company } : c));
            setSnackbar({ open: true, message: response.data.message || 'Empresa atualizada!', severity: 'success' });
            handleCloseEditDialog();
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.message || 'Erro ao atualizar empresa.', severity: 'error' });
        } finally { setSavingEdit(false); }
    };
    const handleEditFormChange = (e) => setEditFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    const handleCloseEditDialog = () => {
        setOpenEditDialog(false); setCompanyToEdit(null);
        setEditFormData({ name: '', licenseType: '' });
    };
    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar({ ...snackbar, open: false });
    };

    const CompanyCard = ({ company }) => (
        <motion.div layout initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <DarkPaper sx={{ p: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Typography variant="h6" fontWeight={600}>{company.name}</Typography>
                    <Stack direction="row">
                        <Tooltip title="Gerenciar Softwares">
                            <IconButton onClick={() => handleOpenSoftwareDialog(company)} color="info" size="small">
                                <Apps fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar Empresa">
                            <IconButton onClick={() => handleEditClick(company)} color="primary" size="small">
                                <Edit fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir Empresa">
                            <IconButton onClick={() => handleDeleteClick(company.id, company.name)} color="error" size="small">
                                <Delete fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>
                <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.05)' }} />
                <Stack spacing={1.5}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">Licença</Typography>
                        <Chip label={company.licenseType} size="small"
                            color={company.licenseType === 'grande' ? 'success' : company.licenseType === 'media' ? 'warning' : 'default'} />
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">Softwares Liberados</Typography>
                        <Chip label={company.softwares?.length || 0} size="small" variant="outlined" sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">Criação</Typography>
                        <Typography variant="body2" fontWeight={500}>{new Date(company.createdAt).toLocaleDateString()}</Typography>
                    </Stack>
                </Stack>

                {company.units && company.units.length > 0 && (
                    <DarkAccordion sx={{ mt: 1, mx: -2, mb: -2 }}>
                        <AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { margin: '8px 0' } }}>
                            <Typography variant="subtitle2" color="text.secondary">Unidades ({company.units.length})</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ padding: '0 16px 16px' }}>
                            <List dense disablePadding>
                                {company.units.map((unit) => {
                                    const userCount = unit._count?.users || 0;
                                    return (
                                        <ListItem key={unit.id} disableGutters sx={{ py: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, mr: 1, overflow: 'hidden' }}>
                                                <ListItemIcon sx={{ minWidth: 32 }}>
                                                    {unit.isMatriz ? <Business fontSize="small" color="primary" /> : <Storefront fontSize="small" sx={{ color: 'text.disabled' }} />}
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={unit.name}
                                                    secondary={`CNPJ: ${unit.cnpj || 'N/A'}`}
                                                    primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium', noWrap: true }}
                                                    secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary', noWrap: true }}
                                                />
                                            </Box>
                                            <Chip
                                                icon={<Group fontSize="small" />}
                                                label={`${userCount}`}
                                                size="small"
                                                variant="outlined"
                                                sx={{ flexShrink: 0, borderColor: 'rgba(255,255,255,0.1)' }}
                                            />
                                        </ListItem>
                                    );
                                })}
                            </List>
                        </AccordionDetails>
                    </DarkAccordion>
                )}
            </DarkPaper>
        </motion.div>
    );

    if (loading) {
        return (
            <ThemeProvider theme={customTheme}>
                <CssBaseline />
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: 'background.default' }}>
                    <CircularProgress />
                </Box>
            </ThemeProvider>
        );
    }
    if (error) {
        return (
            <ThemeProvider theme={customTheme}>
                <CssBaseline />
                <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: 4 }}>
                    <Container maxWidth="xl"><Alert severity="error">{error}</Alert></Container>
                </Box>
            </ThemeProvider>
        );
    }
    if (user && user.role !== 'superadmin') {
        return (
            <ThemeProvider theme={customTheme}>
                <CssBaseline />
                <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: 4 }}>
                    <Container maxWidth="xl"><Alert severity="warning">Você não tem permissão para visualizar esta página.</Alert></Container>
                </Box>
            </ThemeProvider>
        );
    }

    return (
        <ThemeProvider theme={customTheme}>
            <CssBaseline />
            <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: 'background.default', pb: 8 }}>
                <Container maxWidth="xl" sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'stretch', md: 'center' }}
                        spacing={2}
                        sx={{ mb: 4 }}
                    >
                        <Box>
                            <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={700} color="text.primary">Gerenciar Empresas</Typography>
                            <Typography color="text.secondary">Lista todas as empresas cadastradas na plataforma e suas unidades.</Typography>
                        </Box>

                        <Button
                            variant="contained"
                            startIcon={<UploadFile />}
                            onClick={handleOpenBulkSoftwareDialog}
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            Liberar softwares em massa
                        </Button>
                    </Stack>

                    {isMobile ? (
                        <Stack spacing={2}>
                            {companies.length === 0 && !loading ? (
                                <Typography color="text.secondary" textAlign="center" sx={{ py: 4 }}>Nenhuma empresa encontrada.</Typography>
                            ) : (
                                <AnimatePresence>
                                    {companies.map((company) => <CompanyCard key={company.id} company={company} />)}
                                </AnimatePresence>
                            )}
                        </Stack>
                    ) : (
                        <DarkPaper sx={{ borderRadius: 4, overflow: 'hidden' }}>
                            <TableContainer>
                                <Table>
                                    <TableHead sx={{ bgcolor: alpha('#90caf9', 0.04) }}>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Empresa (Matriz)</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Tipo de Licença</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Softwares</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Usuários</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Data de Criação</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }} align="center">Ações</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {companies.length === 0 && !loading ? (
                                            <TableRow>
                                                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                                    <Typography color="text.secondary">Nenhuma empresa encontrada.</Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            <AnimatePresence>
                                                {companies.map((company) => (
                                                    <React.Fragment key={company.id}>
                                                        <motion.tr layout initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                                                            <TableCell><Typography variant="body2" fontWeight="500">{company.name}</Typography></TableCell>
                                                            <TableCell>
                                                                <Chip label={company.licenseType} size="small"
                                                                    color={company.licenseType === 'grande' ? 'success' : company.licenseType === 'media' ? 'warning' : 'default'} />
                                                            </TableCell>
                                                            <TableCell>
                                                                <Tooltip title={company.softwares?.map(s => s.name).join(', ') || "Nenhum"}>
                                                                    <Chip label={company.softwares?.length || 0} size="small" variant="outlined" sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
                                                                </Tooltip>
                                                            </TableCell>
                                                            <TableCell>{company.userCount || 0}</TableCell>
                                                            <TableCell>{new Date(company.createdAt).toLocaleDateString()}</TableCell>
                                                            <TableCell align="center">
                                                                <Tooltip title="Gerenciar Softwares">
                                                                    <IconButton onClick={() => handleOpenSoftwareDialog(company)} color="info" size="small">
                                                                        <Apps fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Tooltip title="Editar Empresa">
                                                                    <IconButton onClick={() => handleEditClick(company)} color="primary" size="small">
                                                                        <Edit fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Tooltip title="Excluir Empresa">
                                                                    <IconButton onClick={() => handleDeleteClick(company.id, company.name)} color="error" size="small">
                                                                        <Delete fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </TableCell>
                                                        </motion.tr>
                                                        {company.units && company.units.length > 0 && (
                                                            <TableRow>
                                                                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                                                                    <DarkAccordion>
                                                                        <AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { margin: '8px 0' } }}>
                                                                            <Typography variant="subtitle2" color="text.secondary">Unidades ({company.units.length})</Typography>
                                                                        </AccordionSummary>
                                                                        <AccordionDetails sx={{ padding: '0 16px 16px' }}>
                                                                            <List dense disablePadding>
                                                                                {company.units.map((unit) => {
                                                                                    const userCount = unit._count?.users || 0;
                                                                                    return (
                                                                                        <ListItem key={unit.id} disableGutters sx={{ py: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                                            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, mr: 1, overflow: 'hidden' }}>
                                                                                                <ListItemIcon sx={{ minWidth: 32 }}>
                                                                                                    {unit.isMatriz ? <Business fontSize="small" color="primary" /> : <Storefront fontSize="small" sx={{ color: 'text.disabled' }} />}
                                                                                                </ListItemIcon>
                                                                                                <ListItemText
                                                                                                    primary={unit.name}
                                                                                                    secondary={`CNPJ: ${unit.cnpj || 'N/A'} | Cidade: ${unit.city || 'N/A'}`}
                                                                                                    primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium', noWrap: true }}
                                                                                                    secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary', noWrap: true }}
                                                                                                />
                                                                                            </Box>
                                                                                            <Chip
                                                                                                icon={<Group fontSize="small" />}
                                                                                                label={`${userCount} ${userCount === 1 ? 'usuário' : 'usuários'}`}
                                                                                                size="small"
                                                                                                variant="outlined"
                                                                                                sx={{ flexShrink: 0, borderColor: 'rgba(255,255,255,0.1)' }}
                                                                                            />
                                                                                        </ListItem>
                                                                                    );
                                                                                })}
                                                                            </List>
                                                                        </AccordionDetails>
                                                                    </DarkAccordion>
                                                                </TableCell>
                                                            </TableRow>
                                                        )}
                                                    </React.Fragment>
                                                ))}
                                            </AnimatePresence>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </DarkPaper>
                    )}
                </Container>

                {/* DIALOG DE LIBERAÇÃO EM MASSA */}
                <Dialog
                    open={openBulkSoftwareDialog}
                    onClose={handleCloseBulkSoftwareDialog}
                    fullWidth maxWidth="md"
                    PaperProps={{ sx: { borderRadius: 3 } }}
                >
                    <DialogTitle>
                        Liberar softwares em massa
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Selecione os softwares e importe um CSV com as empresas que deverão receber o acesso. As permissões atuais serão preservadas.
                        </Typography>
                    </DialogTitle>
                    <DialogContent dividers sx={{ borderColor: 'transparent' }}>
                        <Stack spacing={3}>
                            <Box>
                                <Typography fontWeight={700}>1. Selecione os softwares</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                    Você pode liberar um ou vários softwares de uma vez.
                                </Typography>
                                {allSoftwares.length === 0 ? (
                                    <Alert severity="info">Nenhum software cadastrado no sistema.</Alert>
                                ) : (
                                    <Paper variant="outlined" sx={{ p: 1.5, maxHeight: 220, overflowY: 'auto', bgcolor: 'background.paper', borderColor: 'rgba(255,255,255,0.05)' }}>
                                        <FormGroup>
                                            {allSoftwares.map(software => (
                                                <FormControlLabel
                                                    key={software.id}
                                                    control={
                                                        <Checkbox
                                                            checked={bulkSelectedSoftwareIds.includes(software.id)}
                                                            onChange={() => handleToggleBulkSoftware(software.id)}
                                                        />
                                                    }
                                                    label={
                                                        <Box>
                                                            <Typography variant="body2" fontWeight={600}>{software.name}</Typography>
                                                            {software.description && (
                                                                <Typography variant="caption" color="text.secondary">{software.description}</Typography>
                                                            )}
                                                        </Box>
                                                    }
                                                />
                                            ))}
                                        </FormGroup>
                                    </Paper>
                                )}
                            </Box>

                            <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)' }} />

                            <Box>
                                <Typography fontWeight={700}>2. Baixe o modelo e escolha as empresas</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                    O modelo já vem preenchido com as empresas cadastradas. Apague do arquivo as empresas que não devem receber o software e salve como CSV.
                                </Typography>
                                <Button variant="outlined" startIcon={<Download />} onClick={handleDownloadBulkTemplate}
                                    disabled={companies.length === 0 || bulkProcessing} sx={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                                    Baixar modelo CSV
                                </Button>
                                <Paper variant="outlined" sx={{
                                    mt: 2, p: 2,
                                    bgcolor: 'rgba(255,255,255,0.03)',
                                    borderColor: 'rgba(255,255,255,0.05)',
                                    fontFamily: 'monospace', overflowX: 'auto'
                                }}>
                                    <Typography component="div" variant="caption" sx={{ fontFamily: 'inherit', whiteSpace: 'pre' }}>
                                        {'empresa_id;empresa;cnpj_matriz\n12;Empresa A;12.345.678/0001-90\n27;Empresa B;98.765.432/0001-10'}
                                    </Typography>
                                </Paper>
                            </Box>

                            <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)' }} />

                            <Box>
                                <Typography fontWeight={700}>3. Importe o CSV</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                    A identificação principal é pelo <strong>empresa_id</strong>. O sistema também aceita <strong>cnpj_matriz</strong> como alternativa.
                                </Typography>
                                <Button component="label" variant="outlined" startIcon={<UploadFile />} disabled={bulkProcessing} sx={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                                    Selecionar arquivo CSV                                    <input hidden type="file" accept=".csv,text/csv" onChange={handleBulkCsvFile} />
                                </Button>
                                {bulkCsvFileName && (
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        Arquivo: <strong>{bulkCsvFileName}</strong>
                                    </Typography>
                                )}
                                {bulkCsvError && (<Alert severity="error" sx={{ mt: 1.5 }}>{bulkCsvError}</Alert>)}
                                {bulkCompanyIds.length > 0 && (
                                    <Alert severity="success" sx={{ mt: 1.5 }}>
                                        {bulkCompanyIds.length} empresa{bulkCompanyIds.length === 1 ? '' : 's'} válida{bulkCompanyIds.length === 1 ? '' : 's'} encontrada{bulkCompanyIds.length === 1 ? '' : 's'} no CSV.
                                    </Alert>
                                )}
                                {bulkInvalidRows.length > 0 && (
                                    <Alert severity="warning" sx={{ mt: 1.5 }}>
                                        {bulkInvalidRows.length} linha{bulkInvalidRows.length === 1 ? '' : 's'} não encontrada{bulkInvalidRows.length === 1 ? '' : 's'} e será{bulkInvalidRows.length === 1 ? '' : 'ão'} ignorada{bulkInvalidRows.length === 1 ? '' : 's'}.
                                        {' '}Linhas: {bulkInvalidRows.slice(0, 8).map(item => item.line).join(', ')}
                                        {bulkInvalidRows.length > 8 ? '...' : ''}
                                    </Alert>
                                )}
                            </Box>

                            {bulkProcessing && (
                                <Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        Processando {bulkProgress.processed} de {bulkProgress.total} empresas...
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <CircularProgress size={22} />
                                        <Typography variant="body2">Aplicando permissões sem remover acessos atuais.</Typography>
                                    </Box>
                                </Box>
                            )}
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button onClick={handleCloseBulkSoftwareDialog} disabled={bulkProcessing}>Cancelar</Button>
                        <Button
                            variant="contained"
                            onClick={handleBulkAssignSoftwares}
                            disabled={bulkProcessing || bulkSelectedSoftwareIds.length === 0 || bulkCompanyIds.length === 0}
                            startIcon={bulkProcessing ? <CircularProgress size={20} color="inherit" /> : <Save />}
                        >
                            {bulkProcessing ? 'Liberando...' : `Liberar para ${bulkCompanyIds.length || 0} empresa${bulkCompanyIds.length === 1 ? '' : 's'}`}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* DIALOG DE SOFTWARES POR EMPRESA */}
                <Dialog open={openSoftwareDialog} onClose={handleCloseSoftwareDialog} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
                    <DialogTitle>
                        Softwares Disponíveis
                        <Typography variant="body2" color="text.secondary">
                            Selecione os softwares que a empresa <strong>{companyForSoftware?.name}</strong> terá acesso.
                        </Typography>
                    </DialogTitle>
                    <DialogContent dividers sx={{ borderColor: 'transparent' }}>
                        {allSoftwares.length === 0 ? (
                            <Typography align="center" color="text.secondary" sx={{ py: 2 }}>
                                Nenhum software cadastrado no sistema.
                            </Typography>
                        ) : (
                            <FormGroup>
                                {allSoftwares.map((software) => (
                                    <FormControlLabel
                                        key={software.id}
                                        control={
                                            <Checkbox
                                                checked={selectedSoftwareIds.includes(software.id)}
                                                onChange={() => handleToggleSoftware(software.id)}
                                            />
                                        }
                                        label={
                                            <Box>
                                                <Typography variant="body1">{software.name}</Typography>
                                                {software.description && (
                                                    <Typography variant="caption" color="text.secondary">{software.description}</Typography>
                                                )}
                                            </Box>
                                        }
                                        sx={{ mb: 1, alignItems: 'flex-start' }}
                                    />
                                ))}
                            </FormGroup>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseSoftwareDialog}>Cancelar</Button>
                        <Button onClick={handleSaveSoftwares} variant="contained"
                            startIcon={savingSoftwares ? <CircularProgress size={20} color="inherit" /> : <Save />}
                            disabled={savingSoftwares}>
                            {savingSoftwares ? 'Salvando...' : 'Salvar Permissões'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* DIALOG DE DELETE */}
                <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog} PaperProps={{ sx: { borderRadius: 3 } }}>
                    <DialogTitle fontWeight="bold">Confirmar Exclusão de Empresa</DialogTitle>
                    <DialogContent>
                        <Typography>Você tem certeza que deseja excluir a empresa <Typography component="span" fontWeight="bold">{companyToDeleteName}</Typography>?</Typography>
                        <DialogContentText color="error.main" sx={{ mt: 1 }}>
                            <strong>Atenção:</strong> Todos os usuários e unidades vinculados a esta empresa serão removidos permanentemente.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions sx={{ p: '16px 24px' }}>
                        <Button onClick={handleCloseDeleteDialog}>Cancelar</Button>
                        <Button onClick={handleConfirmDelete} variant="contained" color="error">Confirmar Exclusão</Button>
                    </DialogActions>
                </Dialog>

                {/* DIALOG DE EDITAR */}
                <Dialog open={openEditDialog} onClose={handleCloseEditDialog} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
                    <DialogTitle>{companyToEdit ? `Editar Empresa: ${companyToEdit.name}` : 'Editar Empresa'}</DialogTitle>
                    <DialogContent dividers sx={{ borderColor: 'transparent' }}>
                        <form onSubmit={handleEditSubmit}>
                            <Stack spacing={2} sx={{ mt: 1 }}>
                                <TextField label="Nome da Empresa" name="name" value={editFormData.name} onChange={handleEditFormChange} fullWidth required />
                                <FormControl fullWidth required>
                                    <InputLabel id="edit-license-type-label">Tipo de Licença</InputLabel>
                                    <Select
                                        labelId="edit-license-type-label"
                                        id="edit-license-type"
                                        name="licenseType"
                                        value={editFormData.licenseType}
                                        label="Tipo de Licença"
                                        onChange={handleEditFormChange}
                                    >
                                        <MenuItem value="pequena">Pequena</MenuItem>
                                        <MenuItem value="media">Média</MenuItem>
                                        <MenuItem value="grande">Grande</MenuItem>
                                    </Select>
                                </FormControl>
                                <DialogActions sx={{ px: 0, pb: 0, pt: 2 }}>
                                    <Button onClick={handleCloseEditDialog} color="secondary" disabled={savingEdit}>Cancelar</Button>
                                    <Button type="submit" variant="contained" color="primary" disabled={savingEdit}
                                        startIcon={savingEdit && <CircularProgress size={20} color="inherit" />}>
                                        {savingEdit ? 'Salvando...' : 'Salvar Alterações'}
                                    </Button>
                                </DialogActions>
                            </Stack>
                        </form>
                    </DialogContent>
                </Dialog>

                <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                    <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }} variant="filled">
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        </ThemeProvider>
    );
};

export default CompaniesPage;